/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'antd';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import PropTypes from 'prop-types';
import Container from '../../../components/Container';
import './style.scss';

const OnboardingFileViewer = ({ open, onClose, fileData, mimeType, fileName }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [dataUrl, setDataUrl] = useState(null);
  const [error, setError] = useState(null);

  const isPdfFile = useMemo(() => {
    if (mimeType) {
      return mimeType === 'application/pdf';
    }
    if (fileName) {
      const extension = fileName.toLowerCase().split('.').pop();
      return extension === 'pdf';
    }
    return false;
  }, [mimeType, fileName]);

  const imageMimeType = useMemo(() => {
    if (mimeType && mimeType.startsWith('image/')) {
      return mimeType;
    }
    if (fileName) {
      const extension = fileName.toLowerCase().split('.').pop();
      const mimeTypes = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
      };
      return mimeTypes[extension] || 'image/jpeg';
    }
    return 'image/jpeg';
  }, [mimeType, fileName]);

  useEffect(() => {
    if (!fileData || !isPdfFile) {
      setBlobUrl(null);
      return;
    }

    let url = null;
    try {
      const binaryString = atob(fileData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/pdf' });
      url = URL.createObjectURL(blob);
      setBlobUrl(url);
      setError(null);
    } catch (err) {
      console.error('Error converting base64 to blob:', err);
      setError('Failed to load PDF file');
      setBlobUrl(null);
    }

    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [fileData, isPdfFile]);

  useEffect(() => {
    if (!fileData || isPdfFile) {
      return;
    }

    try {
      const dataUrl = `data:${imageMimeType};base64,${fileData}`;
      setDataUrl(dataUrl);
      setError(null);
    } catch (err) {
      console.error('Error creating data URL:', err);
      setError('Failed to load image file');
    }
  }, [fileData, isPdfFile, imageMimeType]);

  const handleClose = () => {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
    setDataUrl(null);
    setError(null);
    onClose();
  };

  return (
    <Modal
      destroyOnClose
      width={isPdfFile ? '80%' : '650px'}
      className="modal-content-paper onboarding-file-viewer"
      open={open}
      onCancel={handleClose}
      footer={null}
      zIndex={1600}
    >
      <Container widthP={100}>
        {!fileData ? (
          <div className="onboarding-file-viewer-error">
            <p>File data is not available</p>
          </div>
        ) : error ? (
          <div className="onboarding-file-viewer-error">
            <p>{error}</p>
          </div>
        ) : isPdfFile ? (
          blobUrl ? (
            <Worker workerUrl={'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js'}>
              <Viewer fileUrl={blobUrl} />
            </Worker>
          ) : (
            <div className="onboarding-file-viewer-error">
              <p>Loading PDF...</p>
            </div>
          )
        ) : (
          dataUrl ? (
            <img 
              src={dataUrl} 
              alt={fileName || 'Document'} 
              className="onboarding-file-viewer-image" 
            />
          ) : (
            <div className="onboarding-file-viewer-error">
              <p>Loading image...</p>
            </div>
          )
        )}
      </Container>
    </Modal>
  );
};

export default OnboardingFileViewer;

OnboardingFileViewer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  fileData: PropTypes.string,
  mimeType: PropTypes.string,
  fileName: PropTypes.string,
};
